import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Building2, Target, ArrowRight, ArrowLeft, Check, Loader, Sparkles, X, Search, ChevronDown, MessageSquare, Globe } from 'lucide-react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { createOrganization, createSearchTerm, createCompetitor } from '../../firebase/firestore';
import { queueOnboardingCompletionEmail } from '../../firebase/emails';
import { useSearchTermSuggestions } from '../../hooks/useSearchTermSuggestions';
import { suggestCompetitorsAI } from '../../firebase/ai';

// AI Platforms available for tracking
const AI_PLATFORMS = [
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    description: 'OpenAI\'s conversational AI',
    icon: '🤖',
    color: 'from-green-500 to-emerald-600',
    tier: 'free' // Available on all tiers
  },
  {
    id: 'gemini',
    name: 'Google Gemini',
    description: 'Google\'s multimodal AI',
    icon: '✨',
    color: 'from-blue-500 to-indigo-600',
    tier: 'free'
  },
  {
    id: 'claude',
    name: 'Claude',
    description: 'Anthropic\'s helpful AI assistant',
    icon: '🧠',
    color: 'from-orange-500 to-amber-600',
    tier: 'pro' // Requires Pro or higher
  },
  {
    id: 'perplexity',
    name: 'Perplexity',
    description: 'AI-powered search engine',
    icon: '🔍',
    color: 'from-purple-500 to-violet-600',
    tier: 'pro'
  },
  {
    id: 'copilot',
    name: 'Microsoft Copilot',
    description: 'Microsoft\'s AI companion',
    icon: '💼',
    color: 'from-cyan-500 to-teal-600',
    tier: 'business' // Requires Business or higher
  },
  {
    id: 'you',
    name: 'You.com',
    description: 'AI search assistant',
    icon: '🎯',
    color: 'from-pink-500 to-rose-600',
    tier: 'business'
  }
];

// Subscription tier hierarchy (higher index = higher tier)
const TIER_HIERARCHY = ['free', 'pro', 'business', 'enterprise'];

// Check if user's tier has access to a platform
const hasPlatformAccess = (userTier, platformTier) => {
  const userTierIndex = TIER_HIERARCHY.indexOf(userTier || 'free');
  const platformTierIndex = TIER_HIERARCHY.indexOf(platformTier || 'free');
  return userTierIndex >= platformTierIndex;
};

// Comprehensive industry list organized by category
const INDUSTRIES = [
  // Technology
  { value: 'software', label: 'Software & SaaS', category: 'Technology' },
  { value: 'hardware', label: 'Hardware & Electronics', category: 'Technology' },
  { value: 'ai_ml', label: 'AI & Machine Learning', category: 'Technology' },
  { value: 'cybersecurity', label: 'Cybersecurity', category: 'Technology' },
  { value: 'cloud_services', label: 'Cloud Services', category: 'Technology' },
  { value: 'fintech', label: 'Fintech', category: 'Technology' },
  { value: 'healthtech', label: 'Healthtech', category: 'Technology' },
  { value: 'edtech', label: 'Edtech', category: 'Technology' },

  // Finance & Business
  { value: 'banking', label: 'Banking & Financial Services', category: 'Finance' },
  { value: 'insurance', label: 'Insurance', category: 'Finance' },
  { value: 'investment', label: 'Investment & Wealth Management', category: 'Finance' },
  { value: 'accounting', label: 'Accounting & Tax', category: 'Finance' },
  { value: 'consulting', label: 'Consulting', category: 'Professional Services' },
  { value: 'legal', label: 'Legal Services', category: 'Professional Services' },
  { value: 'marketing_agency', label: 'Marketing & Advertising', category: 'Professional Services' },
  { value: 'hr_recruiting', label: 'HR & Recruiting', category: 'Professional Services' },

  // Healthcare
  { value: 'healthcare_provider', label: 'Healthcare Provider', category: 'Healthcare' },
  { value: 'pharmaceutical', label: 'Pharmaceutical', category: 'Healthcare' },
  { value: 'medical_devices', label: 'Medical Devices', category: 'Healthcare' },
  { value: 'biotech', label: 'Biotechnology', category: 'Healthcare' },
  { value: 'mental_health', label: 'Mental Health & Wellness', category: 'Healthcare' },

  // Retail & Consumer
  { value: 'ecommerce', label: 'E-commerce', category: 'Retail' },
  { value: 'retail', label: 'Retail', category: 'Retail' },
  { value: 'fashion', label: 'Fashion & Apparel', category: 'Retail' },
  { value: 'food_beverage', label: 'Food & Beverage', category: 'Consumer Goods' },
  { value: 'consumer_electronics', label: 'Consumer Electronics', category: 'Consumer Goods' },
  { value: 'beauty_cosmetics', label: 'Beauty & Cosmetics', category: 'Consumer Goods' },

  // Industrial & Manufacturing
  { value: 'manufacturing', label: 'Manufacturing', category: 'Industrial' },
  { value: 'automotive', label: 'Automotive', category: 'Industrial' },
  { value: 'aerospace', label: 'Aerospace & Defense', category: 'Industrial' },
  { value: 'energy', label: 'Energy & Utilities', category: 'Industrial' },
  { value: 'construction', label: 'Construction', category: 'Industrial' },
  { value: 'logistics', label: 'Logistics & Supply Chain', category: 'Industrial' },

  // Media & Entertainment
  { value: 'media', label: 'Media & Publishing', category: 'Media & Entertainment' },
  { value: 'entertainment', label: 'Entertainment', category: 'Media & Entertainment' },
  { value: 'gaming', label: 'Gaming', category: 'Media & Entertainment' },
  { value: 'sports', label: 'Sports', category: 'Media & Entertainment' },

  // Real Estate & Property
  { value: 'real_estate', label: 'Real Estate', category: 'Real Estate' },
  { value: 'property_management', label: 'Property Management', category: 'Real Estate' },

  // Education & Non-profit
  { value: 'education', label: 'Education', category: 'Education' },
  { value: 'nonprofit', label: 'Non-profit & NGO', category: 'Non-profit' },
  { value: 'government', label: 'Government', category: 'Government' },

  // Travel & Hospitality
  { value: 'travel', label: 'Travel & Tourism', category: 'Hospitality' },
  { value: 'hospitality', label: 'Hotels & Hospitality', category: 'Hospitality' },
  { value: 'restaurants', label: 'Restaurants & Food Service', category: 'Hospitality' },

  // Other
  { value: 'telecommunications', label: 'Telecommunications', category: 'Other' },
  { value: 'agriculture', label: 'Agriculture', category: 'Other' },
  { value: 'other', label: 'Other', category: 'Other' },
];

// Searchable Industry Select Component
function IndustrySelect({ value, onChange, aiSuggestedIndustry, isLoading }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter industries based on search term
  const filteredIndustries = INDUSTRIES.filter(ind =>
    ind.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ind.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group by category
  const groupedIndustries = filteredIndustries.reduce((acc, ind) => {
    if (!acc[ind.category]) acc[ind.category] = [];
    acc[ind.category].push(ind);
    return acc;
  }, {});

  const selectedIndustry = INDUSTRIES.find(ind => ind.value === value);

  const handleSelect = (industryValue) => {
    onChange(industryValue);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => {
          if (!isLoading) {
            setIsOpen(!isOpen);
            if (!isOpen) setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
        className={`w-full bg-dark-700/50 border rounded-lg px-4 py-3 text-white cursor-pointer flex items-center justify-between transition-all ${
          isOpen ? 'border-primary-500 ring-2 ring-primary-500' : 'border-white/10 hover:border-white/20'
        } ${isLoading ? 'cursor-wait' : ''}`}
      >
        <span className={selectedIndustry ? 'text-white' : 'text-gray-500'}>
          {isLoading ? (
            <span className="flex items-center space-x-2">
              <Loader className="w-4 h-4 animate-spin text-primary-400" />
              <span className="text-primary-300">Detecting industry...</span>
            </span>
          ) : (
            selectedIndustry ? selectedIndustry.label : 'Search or select your industry...'
          )}
        </span>
        {!isLoading && <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-dark-800 border border-white/10 rounded-lg shadow-xl overflow-hidden"
          >
            {/* Search input */}
            <div className="p-3 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search industries..."
                  className="w-full bg-dark-700/50 border border-white/10 rounded-lg pl-10 pr-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* AI suggestion if available */}
            {aiSuggestedIndustry && !value && (
              <div className="px-3 py-2 bg-primary-500/10 border-b border-primary-500/20">
                <p className="text-xs text-primary-300 mb-1">AI Suggested:</p>
                <button
                  onClick={() => {
                    // Try to find matching industry
                    const match = INDUSTRIES.find(ind =>
                      ind.label.toLowerCase().includes(aiSuggestedIndustry.toLowerCase()) ||
                      aiSuggestedIndustry.toLowerCase().includes(ind.label.toLowerCase())
                    );
                    if (match) handleSelect(match.value);
                  }}
                  className="text-sm text-white hover:text-primary-300 transition-colors"
                >
                  {aiSuggestedIndustry}
                </button>
              </div>
            )}

            {/* Industry list */}
            <div className="max-h-64 overflow-y-auto">
              {Object.keys(groupedIndustries).length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-500">
                  No industries found matching "{searchTerm}"
                </div>
              ) : (
                Object.entries(groupedIndustries).map(([category, industries]) => (
                  <div key={category}>
                    <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider bg-dark-700/30">
                      {category}
                    </div>
                    {industries.map((industry) => (
                      <button
                        key={industry.value}
                        onClick={() => handleSelect(industry.value)}
                        className={`w-full px-4 py-2.5 text-left text-sm transition-colors flex items-center justify-between ${
                          value === industry.value
                            ? 'bg-primary-500/20 text-primary-300'
                            : 'text-gray-300 hover:bg-white/5'
                        }`}
                      >
                        <span>{industry.label}</span>
                        {value === industry.value && <Check className="w-4 h-4" />}
                      </button>
                    ))}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function OnboardingFlow() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const userTier = userProfile?.subscriptionTier || userProfile?.plan || 'free';
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: '',
    domains: [], // Array of domains to track
    industry: '',
    role: '',
    primaryGoal: '',
    suggestedTerms: [],
    selectedTerms: [], // Array of { term, platforms: [] }
    selectedPlatforms: ['chatgpt', 'gemini'], // Default to free tier platforms
    competitors: [] // Competitor domains to track
  });

  // AI suggestions hook
  const {
    suggestions: aiSuggestions,
    loading: aiLoading,
    error: aiError,
    generateSuggestions,
    clearSuggestions
  } = useSearchTermSuggestions();

  const steps = [
    { id: 'organization', title: 'Organization', icon: Building2 },
    { id: 'platforms', title: 'AI Platforms', icon: MessageSquare },
    { id: 'terms', title: 'Search Terms', icon: Search },
    { id: 'goals', title: 'Your Goals', icon: Target }
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      if (!user) return;

      // Create organization with platforms and competitors
      const organization = await createOrganization(user.uid, {
        name: formData.organizationName,
        domains: formData.domains,
        industry: formData.industry,
        platforms: formData.selectedPlatforms,
        competitors: formData.competitors
      });

      // Update user document with onboarding data
      await updateDoc(doc(db, 'users', user.uid), {
        onboardingCompleted: true,
        role: formData.role,
        primaryGoal: formData.primaryGoal
      });

      // Create selected search terms with per-term platform configuration
      if (formData.selectedTerms && formData.selectedTerms.length > 0) {
        for (const termData of formData.selectedTerms) {
          const termString = typeof termData === 'string' ? termData : termData.term;
          const platforms = typeof termData === 'object' && termData.platforms
            ? termData.platforms
            : formData.selectedPlatforms;

          await createSearchTerm(organization.id, {
            term: termString,
            userId: user.uid,
            active: true,
            platforms: platforms
          });
        }
      }

      // Create competitor documents in the competitors collection
      if (formData.competitors && formData.competitors.length > 0) {
        for (const competitorDomain of formData.competitors) {
          await createCompetitor(organization.id, {
            name: competitorDomain, // Use domain as name for now
            domain: competitorDomain,
            website: `https://${competitorDomain}`,
            userId: user.uid,
            active: true
          });
        }
      }

      // Queue onboarding completion email
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data();
        await queueOnboardingCompletionEmail(
          user.email,
          userData.displayName || user.displayName,
          formData.organizationName,
          formData.primaryGoal
        );
      } catch (emailError) {
        console.error('Failed to queue onboarding completion email:', emailError);
      }

      navigate('/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle organization name blur to trigger AI suggestions
  const handleOrganizationBlur = async () => {
    if (formData.organizationName.trim().length >= 2) {
      const result = await generateSuggestions(formData.organizationName);
      if (result) {
        // Auto-populate industry if we have a match and user hasn't selected one
        if (result.inferred_industry && !formData.industry) {
          const matchedIndustry = INDUSTRIES.find(
            ind => ind.label.toLowerCase().includes(result.inferred_industry.toLowerCase()) ||
                   result.inferred_industry.toLowerCase().includes(ind.label.toLowerCase())
          );
          if (matchedIndustry) {
            handleChange('industry', matchedIndustry.value);
          }
        }
        // Store suggested terms
        if (result.suggested_search_terms) {
          handleChange('suggestedTerms', result.suggested_search_terms);
          // Pre-select high priority terms with default platforms
          const highPriorityTerms = result.suggested_search_terms
            .filter(t => t.priority === 'high')
            .map(t => ({
              term: t.term,
              platforms: formData.selectedPlatforms // Use currently selected platforms
            }));
          handleChange('selectedTerms', highPriorityTerms);
        }
      }
    }
  };

  // Toggle term selection (new format with platforms)
  const toggleTermSelection = (term) => {
    setFormData(prev => {
      const existingIndex = prev.selectedTerms.findIndex(
        t => (typeof t === 'string' ? t : t.term) === term
      );

      if (existingIndex >= 0) {
        // Remove term
        return {
          ...prev,
          selectedTerms: prev.selectedTerms.filter((_, i) => i !== existingIndex)
        };
      } else {
        // Add term with default platforms
        return {
          ...prev,
          selectedTerms: [
            ...prev.selectedTerms,
            { term, platforms: prev.selectedPlatforms }
          ]
        };
      }
    });
  };

  // Toggle platform for a specific term
  const toggleTermPlatform = (term, platformId) => {
    setFormData(prev => {
      const termIndex = prev.selectedTerms.findIndex(
        t => (typeof t === 'string' ? t : t.term) === term
      );

      if (termIndex < 0) return prev;

      const termData = prev.selectedTerms[termIndex];
      const currentPlatforms = typeof termData === 'object' ? termData.platforms : prev.selectedPlatforms;

      const newPlatforms = currentPlatforms.includes(platformId)
        ? currentPlatforms.filter(p => p !== platformId)
        : [...currentPlatforms, platformId];

      const newSelectedTerms = [...prev.selectedTerms];
      newSelectedTerms[termIndex] = { term, platforms: newPlatforms };

      return { ...prev, selectedTerms: newSelectedTerms };
    });
  };

  // Toggle global platform selection
  const togglePlatform = (platformId) => {
    setFormData(prev => {
      const isSelected = prev.selectedPlatforms.includes(platformId);
      return {
        ...prev,
        selectedPlatforms: isSelected
          ? prev.selectedPlatforms.filter(p => p !== platformId)
          : [...prev.selectedPlatforms, platformId]
      };
    });
  };

  // Check if a term is selected
  const isTermSelected = (term) => {
    return formData.selectedTerms.some(
      t => (typeof t === 'string' ? t : t.term) === term
    );
  };

  // Get platforms for a term
  const getTermPlatforms = (term) => {
    const termData = formData.selectedTerms.find(
      t => (typeof t === 'string' ? t : t.term) === term
    );
    if (!termData) return formData.selectedPlatforms;
    return typeof termData === 'object' ? termData.platforms : formData.selectedPlatforms;
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <OrganizationStep
            formData={formData}
            onChange={handleChange}
            onOrganizationBlur={handleOrganizationBlur}
            aiLoading={aiLoading}
          />
        );
      case 1:
        return (
          <PlatformsStep
            formData={formData}
            onTogglePlatform={togglePlatform}
            userTier={userTier}
          />
        );
      case 2:
        return (
          <SearchTermsStep
            formData={formData}
            aiSuggestions={aiSuggestions}
            aiLoading={aiLoading}
            aiError={aiError}
            onToggleTerm={toggleTermSelection}
            onToggleTermPlatform={toggleTermPlatform}
            isTermSelected={isTermSelected}
            getTermPlatforms={getTermPlatforms}
            onClearSuggestions={clearSuggestions}
            onGenerateSuggestions={() => generateSuggestions(formData.organizationName)}
          />
        );
      case 3:
        return (
          <GoalsStep
            formData={formData}
            onChange={handleChange}
          />
        );
      default:
        return null;
    }
  };

  const isStepValid = () => {
    switch (currentStep) {
      case 0:
        return formData.organizationName && formData.domains.length > 0 && formData.industry;
      case 1:
        return formData.selectedPlatforms.length >= 1; // At least one platform
      case 2:
        return true; // Terms are optional, user can skip
      case 3:
        return formData.primaryGoal;
      default:
        return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-dark-900 text-white">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className="w-10 h-10 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold gradient-text">Deeplexity</span>
          </div>
          <h1 className="text-3xl font-bold mb-2">Welcome to GEO Optimization</h1>
          <p className="text-gray-400">Let's set up your Generative Engine Optimization dashboard</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-12">
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all ${
                  index < currentStep
                    ? 'bg-green-500'
                    : index === currentStep
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500'
                    : 'bg-dark-700 border border-white/10'
                }`}>
                  {index < currentStep ? (
                    <Check className="w-6 h-6 text-white" />
                  ) : (
                    <step.icon className={`w-6 h-6 ${index === currentStep ? 'text-white' : 'text-gray-500'}`} />
                  )}
                </div>
                <p className={`text-sm ${index <= currentStep ? 'text-white' : 'text-gray-500'}`}>
                  {step.title}
                </p>
              </div>
              {index < steps.length - 1 && (
                <div className={`flex-1 h-0.5 mx-4 ${index < currentStep ? 'bg-green-500' : 'bg-dark-700'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-8">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className="flex items-center space-x-2 px-6 py-3 rounded-lg border border-white/20 hover:bg-white/10 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>

          {currentStep < steps.length - 1 ? (
            <button
              onClick={handleNext}
              disabled={!isStepValid()}
              className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ArrowRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleComplete}
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-3 rounded-lg bg-gradient-to-r from-primary-500 to-secondary-500 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{loading ? 'Setting up...' : 'Complete Setup'}</span>
              <Check className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Step Components
function OrganizationStep({ formData, onChange, onOrganizationBlur, aiLoading }) {
  const [domainInput, setDomainInput] = useState('');
  const [competitorInput, setCompetitorInput] = useState('');
  const [competitorSuggestions, setCompetitorSuggestions] = useState([]);
  const [competitorLoading, setCompetitorLoading] = useState(false);

  const addDomain = () => {
    if (domainInput.trim() && !formData.domains.includes(domainInput.trim())) {
      onChange('domains', [...formData.domains, domainInput.trim()]);
      setDomainInput('');
    }
  };

  const removeDomain = (domain) => {
    onChange('domains', formData.domains.filter(d => d !== domain));
  };

  const addCompetitor = () => {
    if (competitorInput.trim() && !formData.competitors.includes(competitorInput.trim())) {
      onChange('competitors', [...formData.competitors, competitorInput.trim()]);
      setCompetitorInput('');
    }
  };

  const addSuggestedCompetitor = (domain) => {
    if (!formData.competitors.includes(domain)) {
      onChange('competitors', [...formData.competitors, domain]);
    }
  };

  const removeCompetitor = (domain) => {
    onChange('competitors', formData.competitors.filter(c => c !== domain));
  };

  const fetchCompetitorSuggestions = async () => {
    if (!formData.organizationName || formData.organizationName.length < 2) return;

    setCompetitorLoading(true);
    try {
      const result = await suggestCompetitorsAI(
        formData.organizationName,
        formData.industry,
        formData.domains[0] || ''
      );
      // Handle nested response structure: { success: true, data: { suggested_competitors: [...] } }
      const competitors = result?.data?.suggested_competitors || result?.suggested_competitors;
      if (competitors) {
        setCompetitorSuggestions(competitors);
      }
    } catch (error) {
      console.error('Error fetching competitor suggestions:', error);
    } finally {
      setCompetitorLoading(false);
    }
  };

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Tell us about your organization</h2>
        <p className="text-gray-400">This helps us personalize your GEO optimization experience</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Organization Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Organization Name *
          </label>
          <div className="relative">
            <input
              type="text"
              value={formData.organizationName}
              onChange={(e) => onChange('organizationName', e.target.value)}
              onBlur={onOrganizationBlur}
              placeholder="e.g., Acme Corporation, Nike, Spotify"
              className="w-full bg-dark-700/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
            />
            {aiLoading && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <Loader className="w-5 h-5 text-primary-400 animate-spin" />
              </div>
            )}
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Enter your company or brand name - we'll use AI to suggest relevant search terms
          </p>
        </div>

        {/* Domains */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Domains to Track *
          </label>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addDomain()}
              placeholder="e.g., acme.com"
              className="flex-1 bg-dark-700/50 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={addDomain}
              disabled={!domainInput.trim()}
              className="px-4 py-2.5 bg-dark-600 rounded-lg text-white hover:bg-dark-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>
          {formData.domains.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.domains.map((domain, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-primary-500/20 border border-primary-500/30 rounded-full text-sm text-primary-300"
                >
                  <Globe className="w-3 h-3" />
                  <span>{domain}</span>
                  <button
                    onClick={() => removeDomain(domain)}
                    className="ml-1 text-primary-400 hover:text-red-400 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Add domains you want to track across AI search engines
          </p>
        </div>

        {/* Industry */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Industry *
          </label>
          <IndustrySelect
            value={formData.industry}
            onChange={(value) => onChange('industry', value)}
            isLoading={aiLoading}
          />
        </div>

        {/* Competitors */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Competitor Domains <span className="text-gray-500">(optional)</span>
          </label>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={competitorInput}
              onChange={(e) => setCompetitorInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCompetitor()}
              placeholder="e.g., competitor.com"
              className="flex-1 bg-dark-700/50 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button
              onClick={addCompetitor}
              disabled={!competitorInput.trim()}
              className="px-4 py-2.5 bg-dark-600 rounded-lg text-white hover:bg-dark-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
          </div>

          {/* AI Competitor Suggestions */}
          {formData.organizationName && formData.organizationName.length >= 2 && (
            <div className="mb-3">
              {competitorSuggestions.length === 0 && !competitorLoading ? (
                <button
                  onClick={fetchCompetitorSuggestions}
                  disabled={competitorLoading}
                  className="inline-flex items-center space-x-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Suggest competitors for "{formData.organizationName}"</span>
                </button>
              ) : competitorLoading ? (
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <Loader className="w-4 h-4 animate-spin text-primary-400" />
                  <span>Finding competitors...</span>
                </div>
              ) : (
                <div className="bg-primary-500/10 border border-primary-500/20 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-primary-300 flex items-center space-x-1">
                      <Sparkles className="w-3 h-3" />
                      <span>AI-Suggested Competitors</span>
                    </p>
                    <button
                      onClick={() => setCompetitorSuggestions([])}
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {competitorSuggestions.map((competitor, idx) => {
                      const domain = typeof competitor === 'string' ? competitor : competitor.domain;
                      const isAdded = formData.competitors.includes(domain);
                      return (
                        <button
                          key={idx}
                          onClick={() => !isAdded && addSuggestedCompetitor(domain)}
                          disabled={isAdded}
                          className={`inline-flex items-center space-x-1 px-3 py-1.5 rounded-full text-sm transition-all ${
                            isAdded
                              ? 'bg-green-500/20 text-green-300 cursor-default'
                              : 'bg-dark-700/50 text-gray-300 hover:bg-primary-500/20 hover:text-primary-300'
                          }`}
                          title={typeof competitor === 'object' ? competitor.reasoning : ''}
                        >
                          {isAdded && <Check className="w-3 h-3" />}
                          <Globe className="w-3 h-3" />
                          <span>{domain}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {formData.competitors.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.competitors.map((domain, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-dark-700/50 rounded-full text-sm text-gray-300"
                >
                  <Globe className="w-3 h-3 text-gray-500" />
                  <span>{domain}</span>
                  <button
                    onClick={() => removeCompetitor(domain)}
                    className="ml-1 text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="mt-2 text-xs text-gray-500">
            Track how competitors appear in AI search results compared to you
          </p>
        </div>

        {/* Role */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Your Role <span className="text-gray-500">(optional)</span>
          </label>
          <input
            type="text"
            value={formData.role}
            onChange={(e) => onChange('role', e.target.value)}
            placeholder="e.g., Marketing Manager, CEO, SEO Specialist"
            className="w-full bg-dark-700/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
          />
        </div>
      </div>
    </div>
  );
}

// AI Platforms Selection Step
function PlatformsStep({ formData, onTogglePlatform, userTier = 'free' }) {
  // Get tier label for display
  const getTierLabel = (tier) => {
    const labels = { free: 'Free', pro: 'Pro', business: 'Business', enterprise: 'Enterprise' };
    return labels[tier] || tier;
  };

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Select AI Platforms to Monitor</h2>
        <p className="text-gray-400">Choose which generative AI platforms you want to track your brand visibility on</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {AI_PLATFORMS.map((platform) => {
          const isSelected = formData.selectedPlatforms.includes(platform.id);
          const hasAccess = hasPlatformAccess(userTier, platform.tier);
          const isLocked = !hasAccess;

          return (
            <button
              key={platform.id}
              onClick={() => hasAccess && onTogglePlatform(platform.id)}
              disabled={isLocked}
              className={`relative p-5 rounded-xl border-2 transition-all text-left ${
                isLocked
                  ? 'border-white/5 bg-dark-700/20 cursor-not-allowed opacity-60'
                  : isSelected
                  ? 'border-primary-500 bg-primary-500/10'
                  : 'border-white/10 hover:border-white/20 bg-dark-700/30'
              }`}
            >
              {/* Lock badge for restricted platforms */}
              {isLocked && (
                <div className="absolute top-3 right-3 flex items-center space-x-1 bg-dark-600/80 px-2 py-1 rounded-full">
                  <span className="text-xs text-gray-400">{getTierLabel(platform.tier)}+</span>
                </div>
              )}
              {/* Check for selected platforms */}
              {isSelected && !isLocked && (
                <div className="absolute top-3 right-3">
                  <Check className="w-5 h-5 text-primary-400" />
                </div>
              )}
              <div className="flex items-start space-x-3">
                <span className={`text-2xl ${isLocked ? 'grayscale' : ''}`}>{platform.icon}</span>
                <div>
                  <h3 className={`font-semibold ${isLocked ? 'text-gray-500' : 'text-white'}`}>{platform.name}</h3>
                  <p className="text-sm text-gray-400 mt-1">{platform.description}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="bg-dark-700/30 rounded-lg p-4 border border-white/5">
        <p className="text-sm text-gray-400">
          <Sparkles className="w-4 h-4 inline-block mr-2 text-primary-400" />
          Selected platforms: <span className="text-white font-medium">{formData.selectedPlatforms.length}</span>
          {formData.selectedPlatforms.length === 0 && (
            <span className="text-yellow-400 ml-2">Please select at least one platform</span>
          )}
        </p>
        {userTier === 'free' && (
          <p className="text-xs text-gray-500 mt-2">
            Upgrade to Pro to unlock Claude, Perplexity, and more platforms
          </p>
        )}
      </div>
    </div>
  );
}

// Search Terms Step with per-term platform configuration
function SearchTermsStep({
  formData,
  aiSuggestions,
  aiLoading,
  aiError,
  onToggleTerm,
  onToggleTermPlatform,
  isTermSelected,
  getTermPlatforms,
  onClearSuggestions,
  onGenerateSuggestions
}) {
  const [customTerm, setCustomTerm] = useState('');
  const [expandedTerm, setExpandedTerm] = useState(null);

  const addCustomTerm = () => {
    if (customTerm.trim()) {
      onToggleTerm(customTerm.trim());
      setCustomTerm('');
    }
  };

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Search Terms to Track</h2>
        <p className="text-gray-400">Select or add search terms/prompts you want to monitor across AI platforms</p>
      </div>

      {/* AI Suggestions Section */}
      {aiSuggestions && aiSuggestions.suggested_search_terms && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/30 rounded-lg p-5 space-y-4"
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-white flex items-center space-x-2">
              <Sparkles className="w-5 h-5 text-primary-400" />
              <span>AI-Suggested Terms for {formData.organizationName}</span>
            </h3>
            <button
              onClick={onClearSuggestions}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {aiSuggestions.company_description && (
            <p className="text-sm text-gray-300">{aiSuggestions.company_description}</p>
          )}

          <div className="flex flex-wrap gap-2">
            {aiSuggestions.suggested_search_terms.map((item, idx) => (
              <button
                key={idx}
                onClick={() => onToggleTerm(item.term)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center space-x-1 ${
                  isTermSelected(item.term)
                    ? 'bg-primary-500 text-white'
                    : 'bg-dark-700/50 text-gray-300 hover:bg-dark-600/50'
                }`}
                title={item.reasoning}
              >
                {isTermSelected(item.term) && <Check className="w-3 h-3" />}
                <span>{item.term}</span>
                <span className={`ml-1 text-xs px-1.5 py-0.5 rounded ${
                  item.priority === 'high' ? 'bg-red-500/30 text-red-300' :
                  item.priority === 'medium' ? 'bg-yellow-500/30 text-yellow-300' :
                  'bg-green-500/30 text-green-300'
                }`}>
                  {item.priority}
                </span>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {/* Generate suggestions button if no suggestions yet */}
      {!aiSuggestions && !aiLoading && formData.organizationName && (
        <button
          onClick={onGenerateSuggestions}
          className="w-full py-3 border border-dashed border-primary-500/50 rounded-lg text-primary-400 hover:bg-primary-500/10 transition-all flex items-center justify-center space-x-2"
        >
          <Sparkles className="w-5 h-5" />
          <span>Generate AI suggestions for "{formData.organizationName}"</span>
        </button>
      )}

      {aiLoading && (
        <div className="flex items-center justify-center py-6 space-x-3 text-gray-300">
          <Loader className="w-5 h-5 animate-spin text-primary-400" />
          <span>Generating suggestions...</span>
        </div>
      )}

      {aiError && (
        <div className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          {aiError}
        </div>
      )}

      {/* Add custom term */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-300">Add Custom Search Term</label>
        <div className="flex space-x-2">
          <input
            type="text"
            value={customTerm}
            onChange={(e) => setCustomTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && addCustomTerm()}
            placeholder="e.g., best software for small business"
            className="flex-1 bg-dark-700/50 border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <button
            onClick={addCustomTerm}
            disabled={!customTerm.trim()}
            className="px-4 py-2.5 bg-primary-500 rounded-lg text-white hover:bg-primary-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add
          </button>
        </div>
      </div>

      {/* Selected Terms with Platform Configuration */}
      {formData.selectedTerms.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-white">Selected Terms ({formData.selectedTerms.length})</h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {formData.selectedTerms.map((termData, idx) => {
              const term = typeof termData === 'string' ? termData : termData.term;
              const platforms = getTermPlatforms(term);
              const isExpanded = expandedTerm === term;

              return (
                <div key={idx} className="bg-dark-700/30 rounded-lg border border-white/5">
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center space-x-3 flex-1">
                      <button
                        onClick={() => onToggleTerm(term)}
                        className="text-red-400 hover:text-red-300 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                      <span className="text-white">{term}</span>
                      <span className="text-xs text-gray-500">
                        {platforms.length} platform{platforms.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <button
                      onClick={() => setExpandedTerm(isExpanded ? null : term)}
                      className="text-gray-400 hover:text-white transition-colors flex items-center space-x-1"
                    >
                      <span className="text-xs">Configure</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {/* Expanded platform configuration */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-white/5 overflow-hidden"
                      >
                        <div className="p-3 flex flex-wrap gap-2">
                          <span className="text-xs text-gray-500 w-full mb-1">Track on:</span>
                          {AI_PLATFORMS.map((platform) => {
                            const isActive = platforms.includes(platform.id);
                            return (
                              <button
                                key={platform.id}
                                onClick={() => onToggleTermPlatform(term, platform.id)}
                                className={`px-2.5 py-1 rounded-full text-xs flex items-center space-x-1 transition-all ${
                                  isActive
                                    ? 'bg-primary-500/20 text-primary-300 border border-primary-500/30'
                                    : 'bg-dark-600/50 text-gray-500 border border-white/5 hover:text-gray-300'
                                }`}
                              >
                                <span>{platform.icon}</span>
                                <span>{platform.name}</span>
                                {isActive && <Check className="w-3 h-3" />}
                              </button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state */}
      {formData.selectedTerms.length === 0 && !aiLoading && (
        <div className="text-center py-8 text-gray-500">
          <Search className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No search terms selected yet</p>
          <p className="text-sm mt-1">Add custom terms or use AI suggestions above</p>
        </div>
      )}
    </div>
  );
}

function GoalsStep({ formData, onChange }) {
  const goals = [
    { id: 'visibility', title: 'Increase Visibility', description: 'Improve rankings across AI-powered search engines' },
    { id: 'brand', title: 'Build Brand Authority', description: 'Establish thought leadership in AI search results' },
    { id: 'traffic', title: 'Drive Traffic', description: 'Generate qualified traffic from generative engines' },
    { id: 'competitive', title: 'Competitive Analysis', description: 'Monitor and outrank competitors in AI search' }
  ];

  return (
    <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-8 space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">What's your primary goal?</h2>
        <p className="text-gray-400">We'll tailor your dashboard to help you achieve this</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {goals.map(goal => (
          <button
            key={goal.id}
            onClick={() => onChange('primaryGoal', goal.id)}
            className={`text-left p-6 rounded-lg border-2 transition-all ${
              formData.primaryGoal === goal.id
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-white/10 hover:border-white/20 bg-dark-700/30'
            }`}
          >
            <h3 className="text-lg font-semibold text-white mb-2">{goal.title}</h3>
            <p className="text-sm text-gray-400">{goal.description}</p>
          </button>
        ))}
      </div>
    </div>
  );
}

export default OnboardingFlow;
